import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type ChangeEvent } from 'react';
import { Alert, Button, ModalActions, Select, TextArea } from '@evogard/ui';
import {
  TICKET_TRANSITIONS,
  ticketStatusLabel,
  validTransitions,
  type TicketStatus,
} from '@evogard/domain';
import type { TicketSummary } from '@evogard/contract';
import { api, ok } from '../../lib/api.js';

/**
 * Mudar a situação — o FORMULÁRIO, sem diálogo próprio (23/09/2026).
 *
 * A rota `changeStatus` existia com permissão e transição validadas, e nenhuma
 * tela a chamava desde que o `StatusField` saiu do detalhe em 09/09: não havia
 * como concluir ou cancelar uma demanda pela interna. Agora ela mora no painel
 * de ações da mesa, como "Encaminhar", e no botão do detalhe — o mesmo
 * formulário nos dois lugares.
 *
 * **As opções vêm de `validTransitions`**, a mesma função que o servidor usa
 * para recusar: a tela nunca oferece uma situação que a gravação recusaria.
 *
 * **O motivo é PÚBLICO.** O servidor o escreve na conversa ("Situação alterada
 * para Cancelada. <motivo>"), sem marca de nota interna — o solicitante e o
 * associado leem. A ajuda do campo diz isso antes de a pessoa escrever. Para
 * cancelar ele é obrigatório, aqui e na gravação.
 */
export function StatusForm({
  ticket,
  onDone,
  initial,
  onBack,
}: {
  ticket: Pick<TicketSummary, 'id' | 'situacao'>;
  /** Fecha o painel — chamado ao gravar e, sem `onBack`, ao voltar. */
  onDone: () => void;
  /** A situação já escolhida — o quadro, quando o cartão foi solto numa coluna. */
  initial?: TicketStatus;
  /** "Voltar" sem mudar nada, quando quem abriu precisa distinguir de gravar. */
  onBack?: () => void;
}) {
  const client = useQueryClient();
  const options = validTransitions(ticket.situacao, TICKET_TRANSITIONS);
  const [status, setStatus] = useState<TicketStatus | ''>(
    initial && options.includes(initial) ? initial : (options[0] ?? ''),
  );
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const change = useMutation({
    mutationFn: async () => {
      const r = await api.tickets.changeStatus({
        params: { id: ticket.id },
        body: { situacao: status as TicketStatus, note: note.trim() || undefined },
      });
      return ok(r, 200, 'Não foi possível mudar a situação.');
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['demandas'] });
      onDone();
    },
    onError: (e: Error) => setError(e.message),
  });

  const cancelling = status === 'cancelada';
  const missingReason = cancelling && note.trim() === '';

  return (
    <div className="flex flex-col gap-4">
      <p className="text-xs text-texto-suave">
        A situação hoje é{' '}
        <strong>{ticketStatusLabel(ticket.situacao).toLowerCase()}</strong>. Concluída e
        cancelada encerram o ticket: depois delas não há volta, e quem precisar continuar
        o assunto abre outra.
      </p>
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          change.mutate();
        }}
      >
        {error && <Alert tone="erro">{error}</Alert>}

        <Select
          label="Nova situação"
          value={status}
          disabled={change.isPending}
          onChange={(e) => setStatus(e.target.value as TicketStatus)}
        >
          {options.map((s) => (
            <option key={s} value={s}>
              {ticketStatusLabel(s)}
            </option>
          ))}
        </Select>

        <TextArea
          label={cancelling ? 'Motivo do cancelamento' : 'Motivo (opcional)'}
          required={cancelling}
          value={note}
          maxLength={2000}
          counter
          rows={3}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setNote(e.target.value)}
          placeholder={
            cancelling ? 'Por que o ticket está sendo cancelado' : 'O que foi feito'
          }
          help="Entra na conversa do ticket: quem abriu e o solicitante leem."
        />

        {/* o botão desabilitado diz por quê — senão é um clique que não responde */}
        {missingReason && (
          <p className="text-xs text-texto-suave">Para cancelar, escreva o motivo.</p>
        )}

        <ModalActions>
          <Button
            type="button"
            variant="discreto"
            size="sm"
            disabled={change.isPending}
            onClick={onBack ?? onDone}
          >
            {/* "Voltar", e não "Cancelar": ao lado de "Cancelar a demanda" seriam dois cancelar */}
            Voltar
          </Button>
          <Button
            type="submit"
            size="sm"
            variant={cancelling ? 'perigo' : 'primario'}
            loading={change.isPending}
            disabled={status === '' || missingReason}
          >
            {cancelling ? 'Cancelar o ticket' : 'Mudar situação'}
          </Button>
        </ModalActions>
      </form>
    </div>
  );
}
